/**
 * Rock Detail View
 *
 * Displays detailed information about a rock, taking over the entire section
 * Handles all rock types: Leadership, GM, and Dept Head
 */

import { Fragment, useState, useRef, useCallback, useEffect, useLayoutEffect, type ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverClose,
} from "@/components/ui/popover";
import {
  Check,
  CheckCircle2,
  ChevronsUpDown,
  ChevronDown,
  ChevronRight,
  Circle,
  Calendar as CalendarIcon,
  Target,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowLeft,
  X,
  Flag,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { patchRockInCache } from "@/contexts/rocks-context";
import { cn } from "@/lib/utils";
import { statusClasses, headerClasses } from "@/lib/ui-theme";
import { DraftText } from "./rock-status";
import { ShortCodeBadge } from "./short-code-badge";
import { useRockDetail } from "./rock-detail-context";
import { Can } from "@/components/can";
import { getL10Teams, getAllL10Teams } from "@/services/profile-services";
import type { L10TeamsResponse } from "@/services/profile-services";

import { formatRelativeTime } from "@/lib/utils";
import type {
  LeadershipRock,
  GMRock,
  DeptRock,
  OrganizationalGoalsData,
  RockMilestone,
  RockDeliverable,
  RockRACI,
  RockBlocker,
  RockDependency,
  WeeklySnapshot,
} from "@/types/rocks";
import { getRockById, getRocksForParent } from "@/lib/load-goals";
import { getStatusColor, getStatusLabel, parseLocalDate } from "@/types/rocks";
import { RockStatusBox } from "./rock-status-box";
import { MilestoneCheckbox } from "./milestone-checkbox";
import { DeliverableCheckbox } from "./deliverable-checkbox";
import { RockHistory } from "./rock-history";
import { WeeklyUpdatesTimeline } from "./weekly-updates-timeline";
import { useUpdateRock, useFetchParentRocks } from "@/hooks/use-rocks-mutations";
import type { ParentRock } from "@/hooks/use-rocks-mutations";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import { DeleteRockDialog } from "./delete-rock-dialog";
import { DraftTextField } from "./draft-text-field";
import { PushRockAction } from "@/components/drives/push-rock-dialog";

type RockType = LeadershipRock | GMRock | DeptRock;

interface RockDetailViewProps {
  rock: RockType;
  data: OrganizationalGoalsData;
  onBack: () => void;
  className?: string;
  /** Callback when the rock is updated by an edit action */
  onRockUpdated?: (rock: RockType) => void;
}


export function RockDetailView({ rock, data, onBack, className, onRockUpdated }: RockDetailViewProps) {
  const [isExiting, setIsExiting] = useState(false);

  const handleBack = () => {
    setIsExiting(true);
    setTimeout(onBack, EXIT_ANIMATION_MS);
  };

  return (
    <Card className={cn(
      "border shadow-sm p-0 gap-0 bg-background duration-200",
      isExiting ? "animate-out slide-out-to-right-4 fade-out" : "animate-in slide-in-from-right-4 fade-in duration-300",
      className
    )}>
      <CardContent className="p-0">
        {/* Back Navigation Header */}
        <div
          className={cn(
            "flex items-center justify-between gap-3 px-4 py-2.5 border-b rounded-t-lg",
            headerClasses.forest
          )}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            disabled={isExiting}
            className="h-7 gap-2 hover:bg-white/10 -my-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Rocks
          </Button>
          {/* Last Updated Indicator */}
          {rock._updated_at && (
            <div className="text-[10px] text-white/70 flex items-center gap-1.5">
              <span>Updated {formatRelativeTime(rock._updated_at)}</span>
              {rock._updated_by && rock._updated_by !== "migration" && (
                <span className="text-white/50">by {rock._updated_by}</span>
              )}
            </div>
          )}
        </div>

        <RockDetailContent key={rock.id} rock={rock} data={data} onRockUpdated={onRockUpdated} />
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Rock Detail Content - reusable inner content for both Card and Dialog views
// =============================================================================

export interface RockDetailContentProps {
  rock: RockType;
  data: OrganizationalGoalsData;
  onRockUpdated?: (rock: RockType) => void;
  className?: string;
  /** Whether the current user can delete this rock */
  canDelete?: boolean;
  /** Number of child rocks that will be orphaned */
  childRockCount?: number;
  /** Callback when the rock is deleted */
  onDeleted?: () => void;
  /** When true, all edit surfaces are disabled (e.g. foreign-lens view) */
  readOnly?: boolean;
}

const EXIT_ANIMATION_MS = 200;
const SAVED_INDICATOR_MS = 2_000;
const NOTIFICATION_MS = 3_000;

export function RockDetailContent({ rock, data, onRockUpdated, className, canDelete, childRockCount = 0, onDeleted, readOnly = false }: RockDetailContentProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blockersExpanded, setBlockersExpanded] = useState(true);
  const [dependenciesExpanded, setDependenciesExpanded] = useState(true);
  const [milestonesExpanded, setMilestonesExpanded] = useState(true);
  const [deliverablesExpanded, setDeliverablesExpanded] = useState(true);

  const isDraft = rock.status === "draft";
  // Operational edits (milestones, deliverables, blockers, deps, notes, title,
  // start_date, raci) stay available through the life of the rock — they're
  // how the owner actually *uses* the rock. The promise (objective, measures,
  // target_date) is locked once out of draft; see LOCKED_POST_DRAFT_FIELDS
  // in rock_repository.py for the backend enforcement.
  const isTerminal = ["complete", "done", "not_done", "dropped"].includes(rock.status);
  // Operational edits are locked when the rock is in a terminal state OR when
  // viewing via a foreign lens (readOnly=true). The foreign-lens gate is the
  // sole FE barrier — RockPolicyService authorizes on person-relationship, not
  // on the meeting's locked team, so it cannot block foreign-lens edits.
  const canEditOperational = !isTerminal && !readOnly;
  // Where a rock sits is alignment, not the committed promise: drafts and
  // committed rocks alike can be re-parented.
  const canReparent = canEditOperational;
  const isCompanyRock = rock.is_company_rock === true;
  const { isNewRock, closeRock, pendingNewRock, setPendingNewRock } = useRockDetail();
  const { mutateAsync: updateRock } = useUpdateRock();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [savedField, setSavedField] = useState<string | null>(null);
  const [savingField, setSavingField] = useState<string | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // L10 teams for team reassignment
  const { data: l10TeamsData } = useQuery<L10TeamsResponse>({
    queryKey: ["l10Teams"],
    queryFn: () => getL10Teams(),
  });
  const { data: allL10TeamsData } = useQuery<L10TeamsResponse>({
    queryKey: ["l10Teams", "all"],
    queryFn: () => getAllL10Teams(),
  });
  const [l10PickerOpen, setL10PickerOpen] = useState(false);

  // Inline notification (replaces global toasts inside the modal)
  const [notification, setNotification] = useState<{ message: string; saving?: boolean } | null>(null);
  const notifyTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const notifySuccess = useCallback((message: string) => {
    if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current);
    setNotification({ message });
    notifyTimerRef.current = setTimeout(() => setNotification(null), NOTIFICATION_MS);
  }, []);
  const notifySaving = useCallback((message: string) => {
    if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current);
    setNotification({ message, saving: true });
  }, []);
  const notifySuccessRef = useRef(notifySuccess);
  notifySuccessRef.current = notifySuccess;
  const notifySavingRef = useRef(notifySaving);
  notifySavingRef.current = notifySaving;

  // Redirect toast.success/info to inline notification while this component is mounted
  useEffect(() => {
    const origSuccess = toast.success;
    const origInfo = toast.info;
    toast.success = ((message: string | React.ReactNode) => {
      if (typeof message === "string") notifySuccessRef.current(message);
    }) as typeof toast.success;
    toast.info = ((message: string | React.ReactNode) => {
      if (typeof message === "string") notifySavingRef.current(message);
    }) as typeof toast.info;
    return () => {
      toast.success = origSuccess;
      toast.info = origInfo;
    };
  }, []);

  // Clean up timers on unmount
  useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current);
  }, []);

  // Inline saving/saved indicator for a specific field
  const savedIndicator = (field: string) =>
    savingField === field ? (
      <span className="text-[10px] text-muted-foreground animate-in fade-in duration-150 ml-auto shrink-0 flex items-center gap-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving
      </span>
    ) : savedField === field ? (
      <span className="text-[10px] text-green-600 dark:text-green-400 animate-in fade-in duration-200 ml-auto shrink-0">
        &#10003; Saved
      </span>
    ) : null;

  // CSS classes to prevent interaction while saving (no visual dimming — optimistic update shows correct data)
  const savingClass = (field: string) =>
    savingField === field ? "pointer-events-none" : "";

  // Keep a ref to the current rock so optimistic rollback always uses the latest pre-edit state
  const rockRef = useRef(rock);
  rockRef.current = rock;

  // Save a field and reflect the change optimistically.
  //
  // The modal derives its rock from the React Query rocks cache, so the
  // optimistic update has to patch the cache (not just call onRockUpdated).
  // For freshly created rocks the cache may not have the rock yet (new-rock
  // race); pendingNewRock acts as the fallback and we patch it too so the
  // optimistic edit shows immediately during that window.
  const saveField = useCallback(async (changes: Record<string, unknown>) => {
    const field = Object.keys(changes)[0] || "title";
    const preEditRock = rockRef.current;
    setSavingField(field);
    // Optimistic update: patch the rock in the React Query cache (the
    // modal's source of truth), patch pendingNewRock if it's the
    // fallback for the same id, and call onRockUpdated for legacy
    // non-modal callers.
    patchRockInCache(queryClient, preEditRock.id, (r) => ({ ...r, ...changes } as typeof r));
    if (pendingNewRock?.id === preEditRock.id) {
      setPendingNewRock({ ...pendingNewRock, ...changes } as RockType);
    }
    if (onRockUpdated) onRockUpdated({ ...preEditRock, ...changes } as RockType);
    try {
      await updateRock({ rockId: preEditRock.id, changes });
      // Don't replace the rock with the server response — the optimistic
      // update already reflects the correct data and a second setState
      // would flash the content as all nested object references change.
      setSavedField(field);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSavedField(null), SAVED_INDICATOR_MS);
    } catch {
      // Revert only the keys we patched — restoring the full pre-edit
      // rock would clobber a concurrent save's optimistic update (which
      // we didn't touch) and leave the UI stale until that save's own
      // settle invalidates the cache.
      const revert = Object.fromEntries(
        Object.keys(changes).map((k) => [k, (preEditRock as unknown as Record<string, unknown>)[k]]),
      );
      patchRockInCache(queryClient, preEditRock.id, (r) => ({ ...r, ...revert } as typeof r));
      if (pendingNewRock?.id === preEditRock.id) {
        setPendingNewRock({ ...pendingNewRock, ...revert } as RockType);
      }
      if (onRockUpdated) onRockUpdated(preEditRock);
    } finally {
      setSavingField(null);
    }
  }, [updateRock, onRockUpdated, queryClient, pendingNewRock, setPendingNewRock]);

  // Parent rock picker state
  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  const [availableParents, setAvailableParents] = useState<ParentRock[]>([]);
  const { mutateAsync: fetchParentRocks } = useFetchParentRocks();

  // Get parent rock info for connections
  const parentRockId =
    "linked_to_parent_rock" in rock
      ? rock.linked_to_parent_rock
      : null;
  const parentRock = parentRockId ? getRockById(data, parentRockId) : null;

  // Get cascaded rocks
  const cascadedRocks = getRocksForParent(data, rock.id);

  // Load available parent rocks when picker opens — scope to this rock's
  // period so the list matches the quarter we're editing in.
  useEffect(() => {
    if (parentPickerOpen && canReparent && rock.profile_id) {
      fetchParentRocks({
        ownerId: rock.profile_id,
        l10TeamId: rock.l10_team_id,
        period: data.period,
        excludeRockId: rock.id,
      })
        .then(setAvailableParents)
        .catch(() => {});
    }
  }, [parentPickerOpen, canReparent, rock.profile_id, rock.l10_team_id, data.period, rock.id, fetchParentRocks]);

  // Get brand info
  const brandInfo = ("brand_name" in rock && rock.brand_name) || null;

  return (
    <div className={cn("relative", className)}>
      {/* Inline notification - sticky + zero-height so it floats without pushing content */}
      <div className="sticky top-0 z-10 h-0 pointer-events-none">
        {notification && (
          <div className="flex justify-end px-4 pt-2">
            <div className="pointer-events-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-muted border text-muted-foreground text-xs font-medium shadow-sm animate-in fade-in slide-in-from-top-1 duration-200">
              {notification.saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" />
              )}
              {notification.message}
            </div>
          </div>
        )}
      </div>

      {/* Title Section - Full Width */}
      <div className={cn("px-4 pt-4 pb-3 border-b", savingClass("title"))}>
        {/* Title Row */}
        <div className="flex items-center gap-2 mb-2">
          <ShortCodeBadge shortCode={rock.short_code} className="text-[10px] shrink-0" />
          {isDraft && !readOnly ? (
            <DraftTextField
              value={rock.title}
              onSave={(v) => saveField({ title: v })}
              className="text-xl font-bold flex-1 px-0 py-0.5"
              placeholder="Rock title"
              required
            />
          ) : (
            <h2 className="text-xl font-bold">{rock.title}</h2>
          )}
          {savedIndicator("title")}
        </div>
        {/* Owner Info */}
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2 flex-wrap">
          <span>{rock.owner}{rock.role ? ` • ${rock.role}` : ""}</span>
          {brandInfo && <span>• {brandInfo}</span>}
          {rock.l10_team && (
            <>
              <span>•</span>
              <Can do="rocks.manage" fallback={
                <span>
                  {rock.l10_team}
                  {allL10TeamsData && rock.l10_team_id && allL10TeamsData.l10_teams.some((t) => t.id === rock.l10_team_id && !t.is_active) && (
                    <span className="text-amber-600 dark:text-amber-400 ml-1">(Inactive)</span>
                  )}
                </span>
              }>
                {readOnly ? (
                  <span>
                    {rock.l10_team}
                    {allL10TeamsData && rock.l10_team_id && allL10TeamsData.l10_teams.some((t) => t.id === rock.l10_team_id && !t.is_active) && (
                      <span className="text-amber-600 dark:text-amber-400 ml-1">(Inactive)</span>
                    )}
                  </span>
                ) : (
                <Popover open={l10PickerOpen} onOpenChange={setL10PickerOpen} modal>
                  <PopoverTrigger asChild>
                    <button className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                      {rock.l10_team}
                      {allL10TeamsData && rock.l10_team_id && allL10TeamsData.l10_teams.some((t) => t.id === rock.l10_team_id && !t.is_active) && (
                        <span className="text-amber-600 dark:text-amber-400">(Inactive)</span>
                      )}
                      <ChevronsUpDown className="h-3 w-3 opacity-50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[280px] max-w-[calc(100vw-2rem)] p-0"
                    align="start"
                    onWheel={(e) => e.stopPropagation()}
                    onOpenAutoFocus={isMobile ? (e) => e.preventDefault() : undefined}
                  >
                    <Command>
                      <CommandInput placeholder="Search teams..." />
                      <CommandList className="max-h-[200px]">
                        <CommandEmpty>No teams found</CommandEmpty>
                        <CommandGroup>
                          {(l10TeamsData?.l10_teams ?? [])
                            .filter((t) => t.is_active)
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((team) => (
                              <CommandItem
                                key={team.id}
                                value={team.name}
                                onSelect={() => {
                                  saveField({ l10_team_id: team.id, l10_team: team.name });
                                  setL10PickerOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", rock.l10_team_id === team.id ? "opacity-100" : "opacity-0")} />
                                {team.name}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                )}
              </Can>
              {savedIndicator("l10_team_id")}
            </>
          )}
          {/* Company Rock toggle — any rock, admin only, not in read-only foreign lens */}
          {!readOnly && (
            <Can do="rocks.manage">
              <span>•</span>
              <button
                type="button"
                onClick={() => {
                  saveField({ is_company_rock: !isCompanyRock });
                }}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs transition-colors border",
                  isCompanyRock
                    ? "bg-primary/10 border-primary/30 text-primary"
                    : "bg-muted/50 border-border text-muted-foreground hover:text-foreground",
                  savingClass("is_company_rock"),
                )}
              >
                {isCompanyRock ? (
                  <CheckCircle2 className="h-2.5 w-2.5" />
                ) : (
                  <Circle className="h-2.5 w-2.5" />
                )}
                Company Rock
              </button>
              {savedIndicator("is_company_rock")}
            </Can>
          )}
        </div>
        {/* Connections Row */}
        {(parentRock || cascadedRocks.length > 0 || canReparent) && (
          <div className="flex items-center gap-3 text-xs">
            {/* Parent rock: an editable rock shows the picker, otherwise read-only */}
            {canReparent ? (
              <Popover open={parentPickerOpen} onOpenChange={setParentPickerOpen} modal>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
                    <ArrowUp className="h-3 w-3" />
                    {parentRock ? (
                      <>
                        <ShortCodeBadge shortCode={parentRock.short_code} className="text-[9px]" />
                        <span className="truncate max-w-[120px]">{parentRock.title}</span>
                      </>
                    ) : (
                      <span className="italic">Standalone (set parent)</span>
                    )}
                    <ChevronsUpDown className="h-3 w-3 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[400px] max-w-[calc(100vw-2rem)] p-0"
                  align="start"
                  onWheel={(e) => e.stopPropagation()}
                  onOpenAutoFocus={isMobile ? (e) => e.preventDefault() : undefined}
                >
                  <Command>
                    <CommandInput placeholder="Search parent rocks..." />
                    <CommandList className="max-h-[250px]">
                      <CommandEmpty>No rocks found</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="__none__"
                          onSelect={() => {
                            saveField({ linked_to_parent_rock: null });
                            setParentPickerOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", !parentRockId ? "opacity-100" : "opacity-0")} />
                          No parent (standalone)
                        </CommandItem>
                      </CommandGroup>
                      <CommandGroup heading="Available Parents">
                        {availableParents.map((pr) => (
                          <CommandItem
                            key={pr.id}
                            value={`${pr.short_code} ${pr.title} ${pr.owner}`}
                            onSelect={() => {
                              saveField({ linked_to_parent_rock: pr.id });
                              setParentPickerOpen(false);
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", parentRockId === pr.id ? "opacity-100" : "opacity-0")} />
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <ShortCodeBadge shortCode={pr.short_code} className="text-[9px]" />
                                <span className="text-xs text-muted-foreground">{pr.owner}</span>
                                {pr.hierarchy_match && <Badge variant="secondary" className="text-[9px]">recommended</Badge>}
                              </div>
                              <span className="text-sm truncate">{pr.title}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : parentRock ? (
              <Popover modal>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
                    <ArrowUp className="h-3 w-3" />
                    <ShortCodeBadge shortCode={parentRock.short_code} className="text-[9px]" />
                    <span className="truncate max-w-[120px]">{parentRock.title}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] max-w-[calc(100vw-2rem)] p-3" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <ArrowUp className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-semibold text-xs">PARENT ROCK</span>
                      <PopoverClose className="ml-auto">
                        <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
                      </PopoverClose>
                    </div>
                    <ParentRockItem rock={parentRock} />
                  </div>
                </PopoverContent>
              </Popover>
            ) : null}
            {(parentRock || canReparent) && cascadedRocks.length > 0 && (
              <span className="text-muted-foreground/50">•</span>
            )}
            {/* Cascaded rocks */}
            {cascadedRocks.length > 0 && (
              <Popover modal={true}>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
                    <ArrowDown className="h-3 w-3" />
                    <span>{cascadedRocks.length} rock{cascadedRocks.length !== 1 ? "s" : ""}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] max-w-[calc(100vw-2rem)] p-3" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <ArrowDown className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-semibold text-xs">CASCADED ROCKS</span>
                      <Badge variant="secondary" className="text-[10px]">
                        {cascadedRocks.length}
                      </Badge>
                      <PopoverClose className="ml-auto">
                        <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
                      </PopoverClose>
                    </div>
                    <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                      {cascadedRocks.map((r) => (
                        <ClickableRockItem key={r.id} rock={r} />
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}
      </div>

      {/* Content — vertically flowed: Status → Objective → Measures (prominent) →
          Milestones + Deliverables → Blockers + Dependencies → RACI → Weekly + History. */}
      <div className="px-4 pt-4 pb-4 space-y-3">

        {/* Status + Objective side-by-side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Status / target date strip */}
          <RockStatusBox
            variant="interactive"
            rock={rock}
            daysElapsed={data.days_elapsed}
            daysTotal={data.days_total}
            period={data.period}
            currentWeek={data.current_week}
            totalWeeks={data.total_weeks}
            daysRemaining={data.days_remaining}
            onRockUpdated={(r) => onRockUpdated?.(r as RockType)}
            onTargetDateChange={isDraft && !readOnly ? ((date) => saveField({ target_date: date })) : undefined}
            savedIndicator={savedIndicator("target_date")}
            statusDisabled={isNewRock || readOnly}
            readOnly={readOnly}
            className={cn("rounded-lg border", savingClass("target_date"))}
          />

          {/* Objective — frozen once committed */}
          <div className={cn("rounded-lg border p-3", savingClass("objective"))}>
            <div className="flex items-center gap-2 mb-1">
              <Target className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-semibold text-xs">OBJECTIVE</span>
              {!isDraft && (
                <span className="text-[9px] uppercase tracking-wide text-muted-foreground/70">Locked</span>
              )}
              {savedIndicator("objective")}
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
              The "why" — what this rock accomplishes and why it matters.
              One or two sentences.
            </p>
            {isDraft && !readOnly ? (
              <DraftTextField
                value={rock.objective}
                onSave={(v) => saveField({ objective: v })}
                className="text-sm text-foreground leading-relaxed rounded-md p-2"
                placeholder="Describe the objective..."
                rows={2}
              />
            ) : (
              <p className="text-sm text-foreground leading-relaxed">
                <DraftText text={rock.objective} isDraft={false} />
              </p>
            )}
          </div>
        </div>

        {/* Measures of Success — prominent, always visible, numbered; frozen once committed */}
        {((rock.measures_of_success && rock.measures_of_success.length > 0) || (isDraft && !readOnly)) && (
          <div className={cn("rounded-lg border-2 border-primary/20 bg-primary/[0.03] p-4", savingClass("measures_of_success"))}>
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <span className="font-semibold text-sm tracking-wide">MEASURES OF SUCCESS</span>
              <Badge variant="secondary" className="text-[10px]">
                {(rock.measures_of_success || []).length}
              </Badge>
              {!isDraft && (
                <span className="text-[9px] uppercase tracking-wide text-muted-foreground/70">Locked</span>
              )}
              {savedIndicator("measures_of_success")}
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed mb-3">
              The concrete, numerical criteria that say you hit the rock. Be
              specific — "Complete 65+ installs" beats "Support installs."
            </p>
            {isDraft && !readOnly ? (
              <EditableStringList
                items={rock.measures_of_success || []}
                onSave={(items) => saveField({ measures_of_success: items })}
                isDraft={true}
                placeholder="Add measure of success..."
                numbered
              />
            ) : (
              <ol className="space-y-2 list-none pl-0">
                {(rock.measures_of_success || []).map((m, i) => (
                  <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-foreground">
                    <span className="shrink-0 text-primary font-semibold tabular-nums" aria-hidden>{i + 1}.</span>
                    <span className="flex-1">{m}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        {/* Milestones + Deliverables — the "how" */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Milestones */}
          {((rock.milestones && rock.milestones.length > 0) || canEditOperational) && (
            <div className={cn("rounded-lg border", savingClass("milestones"))}>
              <button
                onClick={() => setMilestonesExpanded(!milestonesExpanded)}
                className="w-full flex items-center justify-between p-2.5 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {milestonesExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-semibold text-xs">MILESTONES</span>
                  <Badge variant="secondary" className="text-[10px]">
                    {(rock.milestones || []).filter(m => m.completed).length}/{(rock.milestones || []).length}
                  </Badge>
                </div>
                {savedIndicator("milestones")}
              </button>
              {milestonesExpanded && (
                <div className="px-3 pb-3 space-y-2">
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Dated checkpoints that track progress through the quarter. Use the
                    green flag to mark one as your first-week action — the momentum
                    starter you commit to in week 1.
                  </p>
                  <MilestonesList rockId={rock.id} milestones={rock.milestones || []} canEdit={canEditOperational} canToggleCompletion={!isDraft && !isTerminal && !readOnly} onRockUpdated={(r) => onRockUpdated?.(r as RockType)} onSaveMilestones={(milestones) => saveField({ milestones })} />
                </div>
              )}
            </div>
          )}

          {/* Deliverables */}
          {((rock.deliverables && rock.deliverables.length > 0) || canEditOperational) && (
            <div className={cn("rounded-lg border", savingClass("deliverables"))}>
              <button
                onClick={() => setDeliverablesExpanded(!deliverablesExpanded)}
                className="w-full flex items-center justify-between p-2.5 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {deliverablesExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  <Target className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-semibold text-xs">DELIVERABLES</span>
                  <Badge variant="secondary" className="text-[10px]">
                    {(rock.deliverables || []).filter(d => d.completed).length}/{(rock.deliverables || []).length}
                  </Badge>
                </div>
                {savedIndicator("deliverables")}
              </button>
              {deliverablesExpanded && (
                <div className="px-3 pb-3 space-y-2">
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Concrete outputs this rock produces — the tangible artifacts
                    you can point to at quarter-end.
                  </p>
                  <DeliverablesList
                    rockId={rock.id}
                    deliverables={rock.deliverables || []}
                    canEdit={canEditOperational}
                    canToggleCompletion={!isDraft && !isTerminal && !readOnly}
                    onRockUpdated={(r) => onRockUpdated?.(r as RockType)}
                    onSaveDeliverables={(deliverables) => saveField({ deliverables })}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Blockers + Dependencies — the "what's in the way" */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Blockers */}
          <div className={cn("rounded-lg border border-orange-200 dark:border-orange-900", savingClass("blockers"))}>
            <button
              onClick={() => setBlockersExpanded(!blockersExpanded)}
              className="w-full flex items-center justify-between p-2.5 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                {blockersExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5 text-orange-600" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 text-orange-600" />
                )}
                <AlertTriangle className="h-3.5 w-3.5 text-orange-600" />
                <span className="font-semibold text-xs text-orange-600">BLOCKERS</span>
                {(rock.blockers || []).length > 0 && (
                  <Badge variant="destructive" className="text-[10px]">
                    {(rock.blockers || []).length}
                  </Badge>
                )}
              </div>
              {savedIndicator("blockers")}
            </button>
            {blockersExpanded && (
              <div className="px-3 pb-3 space-y-2">
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Active obstacles impeding progress right now. Different from
                  dependencies — blockers need to be resolved, not worked around.
                </p>
                <EditableBlockerList
                  blockers={rock.blockers || []}
                  onSave={(blockers) => saveField({ blockers })}
                  isDraft={canEditOperational}
                />
              </div>
            )}
          </div>

          {/* Dependencies */}
          <div className={cn("rounded-lg border border-sky-200 dark:border-sky-900", savingClass("dependencies"))}>
            <button
              onClick={() => setDependenciesExpanded(!dependenciesExpanded)}
              className="w-full flex items-center justify-between p-2.5 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                {dependenciesExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5 text-sky-600" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 text-sky-600" />
                )}
                <ArrowUp className="h-3.5 w-3.5 text-sky-600" />
                <span className="font-semibold text-xs text-sky-600">DEPENDENCIES</span>
                {(rock.dependencies || []).length > 0 && (
                  <Badge variant="secondary" className="text-[10px]">
                    {(rock.dependencies || []).length}
                  </Badge>
                )}
              </div>
              {savedIndicator("dependencies")}
            </button>
            {dependenciesExpanded && (
              <div className="px-3 pb-3 space-y-2">
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Things you need from others to succeed. For each, note a workaround so
                  you can still deliver if it falls through — dependencies are for planning,
                  not for shifting responsibility.
                </p>
                <EditableDependencyList
                  dependencies={rock.dependencies || []}
                  onSave={(dependencies) => saveField({ dependencies })}
                  isDraft={canEditOperational}
                />
              </div>
            )}
          </div>
        </div>

        {/* RACI — full width, always visible */}
        {(rock.raci || canEditOperational) && (
          <div className={cn("rounded-lg border p-3", savingClass("raci"))}>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-xs">RACI</span>
              {savedIndicator("raci")}
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed mb-3">
              Who's involved. <span className="font-medium">Responsible</span> does the
              work. <span className="font-medium">Accountable</span> owns the outcome
              (usually one person). <span className="font-medium">Consulted</span> gives
              input. <span className="font-medium">Informed</span> is kept in the loop.
            </p>
            {canEditOperational ? (
              <EditableRACIGrid
                raci={rock.raci || {}}
                onSave={(raci) => saveField({ raci })}
              />
            ) : (
              <RACIGrid raci={rock.raci!} />
            )}
          </div>
        )}

        {/* Final Disposition — terminal rocks only */}
        <DispositionCallout rock={rock} />

        {/* Weekly Updates + History — both components render their own
            collapsible header + border, matching visual structure. */}
        {!isDraft && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <WeeklyUpdatesTimeline
              rockId={rock.id}
              period={data.period}
              currentWeek={data.current_week}
              preloadedSnapshots={rock.weekly_snapshots}
            />
            <RockHistory
              rockId={rock.id}
              rockVersion={rock._version}
              preloadedSnapshots={rock.weekly_snapshots}
            />
          </div>
        )}
      </div>

      {!isNewRock && (
        <div className="flex px-4 pt-2">
          <PushRockAction rockId={rock.id} />
        </div>
      )}

      {/* Action Bar */}
      {!readOnly && (isNewRock || canDelete) && (
        <div className="px-4 pb-4 pt-2 border-t mt-4 flex items-center gap-2">
          {isNewRock && (
            <Button
              size="sm"
              onClick={closeRock}
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
              Save
            </Button>
          )}
          {canDelete && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteDialogOpen(true)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950 dark:border-red-800"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete Rock
              </Button>
              <DeleteRockDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                rock={{
                  id: rock.id,
                  short_code: rock.short_code,
                  title: rock.title,
                  owner: rock.owner ?? "",
                  status: rock.status,
                }}
                childRockCount={childRockCount}
                onDeleted={() => {
                  setDeleteDialogOpen(false);
                  onDeleted?.();
                }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Clickable rock item for connection popovers - navigates within the modal
function ClickableRockItem({ rock }: { rock: LeadershipRock | GMRock | DeptRock }) {
  const { navigateToRock } = useRockDetail();
  const statusColor = getStatusColor(rock.status);

  return (
    <div
      className="flex items-start gap-2 p-2 rounded hover:bg-muted/30 transition-colors cursor-pointer"
      onClick={() => navigateToRock(rock)}
    >
      {/* Status Indicator */}
      <div className={cn("h-2 w-2 rounded-full shrink-0 mt-1", statusClasses[statusColor].bgSolid)} />

      {/* Rock Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <ShortCodeBadge shortCode={rock.short_code} className="text-[9px]" />
          <span className="text-xs font-medium text-foreground truncate">{rock.title}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          <span>{rock.owner}</span>
          {rock.role && (
            <>
              <span>•</span>
              <span>{rock.role}</span>
            </>
          )}
          {"brand_code" in rock && rock.brand_code && (
            <>
              <span>•</span>
              <span>{rock.brand_name}</span>
            </>
          )}
        </div>
        {"objective" in rock && (
          <p className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{rock.objective}</p>
        )}
      </div>

      {/* Status Badge */}
      <Badge
        variant="secondary"
        className={cn("text-[9px] shrink-0", statusClasses[statusColor].badge)}
      >
        {getStatusLabel(rock.status)}
      </Badge>
    </div>
  );
}

// Alias for parent rock items (same component, shown with objective)
const ParentRockItem = ClickableRockItem;

// Milestones List Component - grouped by type (first_week_action first, then milestones)
export function MilestonesList({ rockId, milestones, canEdit, canToggleCompletion, onRockUpdated, onSaveMilestones, extraRows = [] }: { rockId: string; milestones: RockMilestone[]; canEdit: boolean; canToggleCompletion: boolean; onRockUpdated?: (rock: unknown) => void; onSaveMilestones?: (milestones: Record<string, unknown>[]) => void; /** Rows placed among the milestones by due date (the Planning page's suggested milestones). */ extraRows?: { key: string; due: string; node: ReactNode }[] }) {
  const listRef = useRef<HTMLDivElement>(null);
  const prevTopsRef = useRef<Map<string, number>>(new Map());

  // A milestone is "first week" if either the new is_first_week flag is set
  // OR the legacy type === "first_week_action" (pre-migration rocks).
  const isFirstWeek = (m: RockMilestone) =>
    m.is_first_week === true || m.type === "first_week_action";

  // Sort: first-week first, then by due date. Single unified list — no headers,
  // just a pill per item.
  const sortByDate = (a: RockMilestone, b: RockMilestone) =>
    parseLocalDate(a.due_date).getTime() - parseLocalDate(b.due_date).getTime();

  const sorted = [...milestones].sort((a, b) => {
    const aFw = isFirstWeek(a);
    const bFw = isFirstWeek(b);
    if (aFw !== bFw) return aFw ? -1 : 1;
    return sortByDate(a, b);
  });

  // Helper to find original index of a milestone (uses reference equality
  // so milestones without ids still get unique indices)
  const getOriginalIndex = (milestone: RockMilestone) => {
    return milestones.indexOf(milestone);
  };

  // Stable key for each milestone (survives reorder)
  const flipKey = (milestone: RockMilestone) =>
    milestone.id || `m-${getOriginalIndex(milestone)}`;

  // Sort order key — FLIP effect only runs when this changes
  const sortOrderKey = sorted.map((m) => flipKey(m)).join(",");

  // FLIP animation: runs only when sort order changes (not on every render)
  useLayoutEffect(() => {
    if (!listRef.current) return;
    const container = listRef.current;
    const containerTop = container.getBoundingClientRect().top;
    const items = container.querySelectorAll<HTMLElement>("[data-flip-key]");
    const prev = prevTopsRef.current;

    if (prev.size > 0 && prev.size === items.length) {
      // Only animate on reorder (same count). Skip on add/delete.
      let maxDy = 0;
      let movedEl: HTMLElement | null = null;
      items.forEach((el) => {
        const key = el.dataset.flipKey!;
        const oldTop = prev.get(key);
        if (oldTop === undefined) return;
        const newTop = el.getBoundingClientRect().top - containerTop;
        const dy = Math.abs(oldTop - newTop);
        if (dy > maxDy) {
          maxDy = dy;
          movedEl = el;
        }
      });

      if (movedEl && maxDy >= 1) {
        const key = (movedEl as HTMLElement).dataset.flipKey!;
        const oldTop = prev.get(key)!;
        const newTop = (movedEl as HTMLElement).getBoundingClientRect().top - containerTop;
        const dy = oldTop - newTop;
        (movedEl as HTMLElement).style.transform = `translateY(${dy}px)`;
        (movedEl as HTMLElement).style.transition = "none";
        void (movedEl as HTMLElement).offsetHeight;
        (movedEl as HTMLElement).style.transition = "transform 300ms ease";
        (movedEl as HTMLElement).style.transform = "";
      }
    }

    // Record container-relative positions for next order change
    const tops = new Map<string, number>();
    items.forEach((el) => {
      tops.set(el.dataset.flipKey!, el.getBoundingClientRect().top - containerTop);
    });
    prevTopsRef.current = tops;
  }, [sortOrderKey]);

  // Helper to serialize milestones for saving. Preserves is_first_week and
  // drops the legacy type — future writes are all is_first_week-based.
  const serializeMilestones = (ms: RockMilestone[]) =>
    ms.map((m) => ({
      description: m.description,
      due_date: m.due_date,
      completed: m.completed,
      is_first_week: isFirstWeek(m),
    }));

  const handleDelete = (milestone: RockMilestone) => {
    if (!onSaveMilestones) return;
    const updated = milestones.filter(m => m !== milestone);
    onSaveMilestones(serializeMilestones(updated));
  };

  const toggleFirstWeek = (milestone: RockMilestone) => {
    if (!onSaveMilestones) return;
    const idx = getOriginalIndex(milestone);
    const updated = [...milestones];
    updated[idx] = { ...updated[idx], is_first_week: !isFirstWeek(milestone) };
    onSaveMilestones(serializeMilestones(updated));
  };

  // Extra rows go before the first dated milestone due after them; first-week items stay on top.
  const extrasBefore = (milestone: RockMilestone | null) =>
    extraRows.filter((row) => {
      const due = parseLocalDate(row.due).getTime();
      const slot = sorted.findIndex((m) => !isFirstWeek(m) && parseLocalDate(m.due_date).getTime() > due);
      return milestone === null ? slot === -1 : sorted[slot] === milestone;
    });

  return (
    <div ref={listRef} className="space-y-1.5">
      {sorted.map((milestone) => {
        const fw = isFirstWeek(milestone);
        const before = extrasBefore(milestone);
        return (
          <Fragment key={flipKey(milestone)}>
          {before.map((row) => <Fragment key={row.key}>{row.node}</Fragment>)}
          <div data-flip-key={flipKey(milestone)} className="group/milestone flex items-start gap-1">
            <div className="flex-1 min-w-0">
              <MilestoneCheckbox
                rockId={rockId}
                milestone={milestone}
                milestoneIndex={getOriginalIndex(milestone)}
                canEdit={canEdit}
                canToggleCompletion={canToggleCompletion}
                onRockUpdated={onRockUpdated}
                onEdit={canEdit && onSaveMilestones ? (changes) => {
                  const idx = getOriginalIndex(milestone);
                  const updated = [...milestones];
                  updated[idx] = { ...updated[idx], ...changes };
                  onSaveMilestones(serializeMilestones(updated));
                } : undefined}
              />
            </div>
            {canEdit ? (
              <>
                <button
                  onClick={() => toggleFirstWeek(milestone)}
                  className={cn(
                    "shrink-0 p-1 transition-colors",
                    fw
                      ? "text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400"
                      : "text-muted-foreground/30 hover:text-green-600 opacity-0 group-hover/milestone:opacity-100 focus:opacity-100",
                  )}
                  title={fw ? "Unflag as first-week action" : "Mark as first-week action"}
                >
                  <Flag className={cn("h-3.5 w-3.5", fw && "fill-green-600 dark:fill-green-500")} />
                </button>
                <button
                  onClick={() => handleDelete(milestone)}
                  className="shrink-0 p-1 text-muted-foreground hover:text-red-500 transition-colors opacity-0 group-hover/milestone:opacity-100 focus:opacity-100"
                  title="Delete milestone"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </>
            ) : fw ? (
              <span
                className="shrink-0 mt-1 inline-flex"
                title="First-week action"
                aria-label="First-week action"
              >
                <Flag className="h-3.5 w-3.5 text-green-600 fill-green-600 dark:text-green-500 dark:fill-green-500" />
              </span>
            ) : null}
          </div>
          </Fragment>
        );
      })}
      {extrasBefore(null).map((row) => <Fragment key={row.key}>{row.node}</Fragment>)}

      {/* Add Milestone */}
      {canEdit && onSaveMilestones && (
        <AddMilestoneButton
          onAdd={(desc, dueDate) => {
            const newMilestone: RockMilestone = {
              description: desc,
              due_date: dueDate,
              completed: false,
              is_first_week: false,
            };
            onSaveMilestones(serializeMilestones([...milestones, newMilestone]));
          }}
        />
      )}
    </div>
  );
}

/** The deliverables checklist (toggle, inline edit, delete, add). Shared with the Planning page's left rail. */
export function DeliverablesList({ rockId, deliverables, canEdit, canToggleCompletion, onRockUpdated, onSaveDeliverables }: { rockId: string; deliverables: RockDeliverable[]; canEdit: boolean; canToggleCompletion: boolean; onRockUpdated?: (rock: unknown) => void; onSaveDeliverables?: (deliverables: { description: string; completed: boolean }[]) => void }) {
  return (
    <>
      <ul className="space-y-2">
        {deliverables.map((deliverable, idx) => (
          <li key={idx} className="flex items-center gap-1">
            <div className="flex-1">
              <DeliverableCheckbox
                rockId={rockId}
                deliverableIndex={idx}
                deliverable={deliverable}
                canEdit={canEdit}
                canToggleCompletion={canToggleCompletion}
                onRockUpdated={onRockUpdated}
                onEdit={canEdit && onSaveDeliverables ? (changes) => {
                  const updated = deliverables.map((d, i) =>
                    i === idx ? { description: changes.description ?? d.description, completed: d.completed } : { description: d.description, completed: d.completed }
                  );
                  onSaveDeliverables(updated);
                } : undefined}
              />
            </div>
            {canEdit && onSaveDeliverables && (
              <button
                onClick={() => {
                  const updated = deliverables.filter((_, i) => i !== idx);
                  onSaveDeliverables(updated.map((d) => ({ description: d.description, completed: d.completed })));
                }}
                className="shrink-0 p-1 text-muted-foreground hover:text-red-500 transition-colors"
                title="Delete deliverable"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {canEdit && onSaveDeliverables && (
        <AddItemButton
          placeholder="Add deliverable..."
          onAdd={(desc) => {
            const updated = [...deliverables.map(d => ({ description: d.description, completed: d.completed })), { description: desc, completed: false }];
            onSaveDeliverables(updated);
          }}
        />
      )}
    </>
  );
}

// RACI Grid Component - Compact grid display
function RACIGrid({ raci }: { raci: RockRACI }) {
  const roles: Array<{ key: keyof RockRACI; label: string; color: string }> = [
    { key: "responsible", label: "R", color: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" },
    { key: "accountable", label: "A", color: "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300" },
    { key: "consulted", label: "C", color: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" },
    { key: "informed", label: "I", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
  ];

  const roleLabels: Record<keyof RockRACI, string> = {
    responsible: "Responsible",
    accountable: "Accountable",
    consulted: "Consulted",
    informed: "Informed",
  };

  return (
    <div className="grid grid-cols-4 gap-2">
      {roles.map(({ key, label, color }) => {
        const raw = raci[key];
        const people = Array.isArray(raw) ? raw : raw ? [String(raw)] : [];
        if (people.length === 0) return null;

        return (
          <div key={key} className="space-y-1">
            <div className="flex items-center gap-1">
              <Badge variant="secondary" className={cn("text-[10px] px-1.5 py-0", color)}>
                {label}
              </Badge>
              <span className="text-[10px] text-muted-foreground">{roleLabels[key]}</span>
            </div>
            <div className="space-y-0.5">
              {people.map((person, idx) => (
                <div key={idx} className="text-xs text-muted-foreground truncate" title={person}>
                  {person}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// Draft Editing Helper Components
// =============================================================================


/** Editable list of strings with add/delete */
function EditableStringList({ items, onSave, isDraft, placeholder, bulletChar = "•", numbered = false }: {
  items: string[];
  onSave: (items: string[]) => void;
  isDraft: boolean;
  placeholder?: string;
  bulletChar?: string;
  numbered?: boolean;
}) {
  const marker = (idx: number) =>
    numbered ? <span className="text-primary font-semibold tabular-nums">{idx + 1}.</span> : <span className="text-muted-foreground">{bulletChar}</span>;
  if (!isDraft) {
    const ListTag = numbered ? "ol" : "ul";
    return (
      <ListTag className="space-y-1.5">
        {items.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm">
            <span className="mt-0.5">{marker(idx)}</span>
            <span className="text-foreground flex-1">{item}</span>
          </li>
        ))}
      </ListTag>
    );
  }

  return (
    <div className="space-y-1.5">
      {items.map((item, idx) => (
        <div key={idx} className="flex items-start gap-2 text-sm group">
          <span className="mt-1.5">{marker(idx)}</span>
          <DraftTextField
            value={item}
            onSave={(v) => {
              if (v) {
                const updated = [...items];
                updated[idx] = v;
                onSave(updated);
              } else {
                onSave(items.filter((_, i) => i !== idx));
              }
            }}
            className="flex-1 text-sm text-foreground py-0.5"
          />
          <button
            onClick={() => onSave(items.filter((_, i) => i !== idx))}
            className="shrink-0 p-0.5 text-muted-foreground hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
            title="Delete"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ))}
      <AddItemButton placeholder={placeholder} onAdd={(v) => onSave([...items, v])} />
    </div>
  );
}

/** Add item button with inline input */
function AddItemButton({ placeholder, onAdd }: {
  placeholder?: string;
  onAdd: (value: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!adding) {
    return (
      <button
        onClick={() => { setAdding(true); setTimeout(() => inputRef.current?.focus(), 0); }}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mt-2"
      >
        <Plus className="h-3 w-3" />
        {placeholder || "Add item..."}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 mt-2">
      <Plus className="h-3 w-3 text-muted-foreground shrink-0" />
      <input
        ref={inputRef}
        autoFocus
        placeholder={placeholder}
        className="flex-1 bg-transparent border-b border-primary focus:outline-none text-base md:text-sm py-0.5"
        onBlur={(e) => {
          const v = e.target.value.trim();
          if (v) onAdd(v);
          setAdding(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            const v = (e.target as HTMLInputElement).value.trim();
            if (v) { onAdd(v); (e.target as HTMLInputElement).value = ""; }
          }
          if (e.key === "Escape") setAdding(false);
        }}
      />
    </div>
  );
}

/** Add milestone button with description + date inputs */
function AddMilestoneButton({ onAdd }: {
  onAdd: (description: string, dueDate: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [desc, setDesc] = useState("");
  const [dueDate, setDueDate] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  if (!adding) {
    return (
      <button
        onClick={() => { setAdding(true); setTimeout(() => inputRef.current?.focus(), 0); }}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mt-2"
      >
        <Plus className="h-3 w-3" />
        Add milestone...
      </button>
    );
  }

  const handleAdd = () => {
    if (desc.trim() && dueDate) {
      onAdd(desc.trim(), dueDate);
      setDesc("");
      setDueDate("");
      setAdding(false);
    }
  };

  return (
    <div className="flex items-center gap-2 mt-2">
      <Plus className="h-3 w-3 text-muted-foreground shrink-0" />
      <input
        ref={inputRef}
        autoFocus
        placeholder="Milestone description..."
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        className="flex-1 bg-transparent border-b border-primary focus:outline-none text-base md:text-sm py-0.5"
        onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); if (e.key === "Escape") setAdding(false); }}
      />
      <Popover modal>
        <PopoverTrigger asChild>
          <button className="inline-flex items-center text-xs px-2 py-0.5 rounded-md border border-dashed border-primary/50 hover:bg-muted/50 transition-colors">
            <CalendarIcon className="h-3 w-3 mr-1" />
            {dueDate
              ? new Date(dueDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })
              : "Date"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={dueDate ? new Date(dueDate + "T00:00:00") : undefined}
            defaultMonth={dueDate ? new Date(dueDate + "T00:00:00") : undefined}
            onSelect={(date) => {
              if (date) setDueDate(date.toISOString().split("T")[0]);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <Button variant="outline" size="sm" className="h-6 text-xs" onClick={handleAdd} disabled={!desc.trim() || !dueDate}>
        Add
      </Button>
      <button onClick={() => setAdding(false)} className="text-muted-foreground hover:text-foreground">
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

/** Editable blocker list with severity selector */
function EditableBlockerList({ blockers, onSave, isDraft }: {
  blockers: RockBlocker[];
  onSave: (blockers: RockBlocker[]) => void;
  isDraft: boolean;
}) {
  if (!isDraft) {
    return (
      <>
        {blockers.map((blocker, idx) => (
          <div key={idx} className="flex items-start gap-2 p-2 rounded-md bg-orange-50 dark:bg-orange-950/20">
            <Badge
              variant={blocker.severity === "high" ? "destructive" : blocker.severity === "medium" ? "default" : "secondary"}
              className="text-[10px] shrink-0"
            >
              {blocker.severity}
            </Badge>
            <div className="text-sm text-muted-foreground">{blocker.description}</div>
          </div>
        ))}
      </>
    );
  }

  return (
    <div className="space-y-2">
      {blockers.map((blocker, idx) => (
        <div key={idx} className="flex items-start gap-2 p-2 rounded-md bg-orange-50 dark:bg-orange-950/20 group">
          <select
            value={blocker.severity}
            onChange={(e) => {
              const updated = [...blockers];
              updated[idx] = { ...updated[idx], severity: e.target.value as RockBlocker["severity"] };
              onSave(updated);
            }}
            className="text-[10px] bg-transparent border rounded px-1 py-0.5 shrink-0"
          >
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
          </select>
          <DraftTextField
            value={blocker.description}
            onSave={(v) => {
              if (v && v !== blocker.description) {
                const updated = [...blockers];
                updated[idx] = { ...updated[idx], description: v };
                onSave(updated);
              }
            }}
            className="flex-1 text-sm text-muted-foreground"
            required
          />
          <button
            onClick={() => onSave(blockers.filter((_, i) => i !== idx))}
            className="shrink-0 p-0.5 text-muted-foreground hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
            title="Delete blocker"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ))}
      <AddItemButton
        placeholder="Add blocker..."
        onAdd={(desc) => onSave([...blockers, { description: desc, severity: "medium" }])}
      />
    </div>
  );
}

/** Editable dependency list — description, optional source, and required workaround. */
function EditableDependencyList({ dependencies, onSave, isDraft }: {
  dependencies: RockDependency[];
  onSave: (dependencies: RockDependency[]) => void;
  isDraft: boolean;
}) {
  if (!isDraft) {
    if (dependencies.length === 0) {
      return <p className="text-sm text-muted-foreground italic">None noted.</p>;
    }
    return (
      <>
        {dependencies.map((dep, idx) => (
          <div key={idx} className="rounded-md bg-sky-50 dark:bg-sky-950/20 p-2 space-y-1">
            <div className="text-sm text-foreground">{dep.description}</div>
            <div className="text-[11px] text-muted-foreground">
              <span className="font-medium">Workaround:</span> {dep.workaround || "—"}
            </div>
          </div>
        ))}
      </>
    );
  }

  return (
    <div className="space-y-2">
      {dependencies.map((dep, idx) => (
        <div key={idx} className="rounded-md bg-sky-50 dark:bg-sky-950/20 p-2 space-y-1.5 group">
          <div className="flex items-start gap-2">
            <DraftTextField
              value={dep.description}
              onSave={(v) => {
                if (v !== dep.description) {
                  const updated = [...dependencies];
                  updated[idx] = { ...updated[idx], description: v };
                  onSave(updated);
                }
              }}
              className="flex-1 text-sm text-foreground"
              placeholder="What you need..."
              required
            />
            <button
              onClick={() => onSave(dependencies.filter((_, i) => i !== idx))}
              className="shrink-0 p-0.5 text-muted-foreground hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
              title="Delete dependency"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
          <div className="flex items-center gap-2 text-[11px]">
            <span className="shrink-0 text-muted-foreground font-medium w-20">Workaround</span>
            <DraftTextField
              value={dep.workaround}
              onSave={(v) => {
                if (v !== dep.workaround) {
                  const updated = [...dependencies];
                  updated[idx] = { ...updated[idx], workaround: v };
                  onSave(updated);
                }
              }}
              className="flex-1 text-[11px] text-muted-foreground"
              placeholder="What you'll do if it falls through"
              required
            />
          </div>
        </div>
      ))}
      <AddItemButton
        placeholder="Add dependency..."
        onAdd={(desc) =>
          onSave([...dependencies, { description: desc, workaround: "" }])
        }
      />
    </div>
  );
}

/** Editable RACI grid - comma-separated inputs for each role */
function EditableRACIGrid({ raci, onSave }: {
  raci: RockRACI;
  onSave: (raci: RockRACI) => void;
}) {
  const roles: Array<{ key: keyof RockRACI; label: string; fullLabel: string; color: string }> = [
    { key: "responsible", label: "R", fullLabel: "Responsible", color: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" },
    { key: "accountable", label: "A", fullLabel: "Accountable", color: "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300" },
    { key: "consulted", label: "C", fullLabel: "Consulted", color: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" },
    { key: "informed", label: "I", fullLabel: "Informed", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
  ];

  return (
    <div className="grid grid-cols-2 gap-3">
      {roles.map(({ key, label, fullLabel, color }) => {
        const raw = raci[key];
        const people = Array.isArray(raw) ? raw : raw ? [String(raw)] : [];

        return (
          <div key={key} className="space-y-1">
            <div className="flex items-center gap-1">
              <Badge variant="secondary" className={cn("text-[10px] px-1.5 py-0", color)}>
                {label}
              </Badge>
              <span className="text-[10px] text-muted-foreground">{fullLabel}</span>
            </div>
            <DraftTextField
              value={people.join(", ")}
              onSave={(v) => {
                const names = v.split(",").map(s => s.trim()).filter(Boolean);
                const updated = { ...raci, [key]: names.length > 0 ? names : undefined };
                for (const k of Object.keys(updated) as (keyof RockRACI)[]) {
                  if (!updated[k] || (Array.isArray(updated[k]) && (updated[k] as string[]).length === 0)) {
                    delete updated[k];
                  }
                }
                onSave(updated);
              }}
              className="text-xs py-0.5"
              placeholder={`Add ${fullLabel.toLowerCase()}...`}
            />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DispositionCallout — shows final narrative for terminal rocks
// ---------------------------------------------------------------------------

const TERMINAL_STATUSES = new Set(["complete", "done", "not_done", "dropped"]);

function DispositionCallout({ rock }: { rock: RockType }) {
  if (!TERMINAL_STATUSES.has(rock.status)) return null;
  // Disposition is written to the deterministic slot at `total_weeks + 1`, so
  // prefer the terminal snapshot with the highest week_number rather than the
  // first one — there can be stale terminal snapshots from earlier transitions.
  const snap = (rock.weekly_snapshots ?? [])
    .filter((s) => TERMINAL_STATUSES.has(s.health))
    .reduce<WeeklySnapshot | null>(
      (latest, s) => (!latest || s.week_number > latest.week_number ? s : latest),
      null,
    );
  if (!snap?.notes) return null;

  const isComplete = rock.status === "complete" || rock.status === "done";
  const isDropped = rock.status === "dropped";
  const label = isComplete ? "Marked Complete" : isDropped ? "Dropped" : "Not Done";
  const Icon = isComplete ? CheckCircle2 : isDropped ? Circle : X;
  const tone = isComplete
    ? "bg-[#28A745]/10 border-[#28A745]/30 text-[#1E7E34] dark:text-[#5DD879]"
    : isDropped
      ? "bg-muted/40 border-muted-foreground/20 text-muted-foreground"
      : "bg-[#DC3545]/10 border-[#DC3545]/30 text-[#C82333] dark:text-[#F5A3A9]";

  return (
    <div className={cn("rounded-lg border p-3", tone)}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="h-3.5 w-3.5" />
        <span className="font-semibold text-xs uppercase tracking-wide">{label}</span>
        {snap.saved_by && (
          <span className="ml-auto text-[10px] opacity-70">
            by {snap.saved_by}
          </span>
        )}
      </div>
      <p className="text-sm whitespace-pre-wrap text-foreground">{snap.notes}</p>
    </div>
  );
}
